import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import AutocompleteContainer from '@/components/shared/Autocomplete/components/AutocompleteContainer';
import AutocompleteFilterButton from '@/components/shared/Autocomplete/components/AutocompleteFilterButton';
import AutocompleteWithLabel from '@/components/shared/Autocomplete/components/AutocompleteWithLabel';
import { useDistributorGroupsAutocomplete } from '@/hooks/autocomplete/useDistributorGroupsAutocomplete';
import { type UserAutocompleteResult, useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import { components } from '@/shared/api/openapi';

export type DistributorGroup = components['schemas']['GetDistributorsEndpoint_DistributorResponse'];

export interface DistributorsFiltersAutocompleteProps {
  organizationId: number;
  disabled?: boolean;
}

const parseGroupsParam = (searchParams: URLSearchParams): DistributorGroup[] => {
  const groupsParam = searchParams.get('groups');
  if (!groupsParam) return [];
  return groupsParam
    .split(',')
    .filter(Boolean)
    .map((distributorName) => ({ distributorName }));
};

const parseEmailsParam = (searchParams: URLSearchParams): UserAutocompleteResult[] => {
  const emailParam = searchParams.get('emails');
  if (!emailParam) return [];
  return emailParam
    .split(',')
    .filter(Boolean)
    .map((email) => ({ email }));
};

function DistributorsFiltersAutocomplete({ organizationId, disabled }: DistributorsFiltersAutocompleteProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedGroups, setSelectedGroups] = useState<DistributorGroup[]>(parseGroupsParam(searchParams));
  const [selectedEmails, setSelectedEmails] = useState<UserAutocompleteResult[]>(parseEmailsParam(searchParams));

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);

  const [groupInputValue, setGroupInputValue] = useState('');
  const [groupDebouncedValue, setGroupDebouncedValue] = useState('');

  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailDebouncedValue, setEmailDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setGroupDebouncedValue(groupInputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [groupInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => setEmailDebouncedValue(emailInputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [emailInputValue]);

  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    isRefetching: isGroupsRefetching,
  } = useDistributorGroupsAutocomplete({
    organizationId,
    filter: groupDebouncedValue,
    enabled: !disabled,
  });

  const {
    data: emailsData,
    isLoading: isEmailsLoading,
    isRefetching: isEmailsRefetching,
  } = useUsersAutocomplete({
    organizationId,
    filter: emailDebouncedValue,
    enabled: !disabled,
  });

  const handleGroupsChange = (_: React.SyntheticEvent, groups: DistributorGroup[]) => {
    setSelectedGroups(groups);
    if (groups.length === 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('groups');
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleEmailsChange = (_: React.SyntheticEvent, emails: UserAutocompleteResult[]) => {
    setSelectedEmails(emails);
    if (emails.length === 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('emails');
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleFilterApply = () => {
    const params = new URLSearchParams(searchParams);

    if (selectedGroups.length > 0) {
      const groupNames = selectedGroups.map((g) => g.distributorName).filter((n): n is string => Boolean(n));
      if (groupNames.length > 0) {
        params.set('groups', groupNames.join(','));
      } else {
        params.delete('groups');
      }
    } else {
      params.delete('groups');
    }

    if (selectedEmails.length > 0) {
      const emails = selectedEmails.map((e) => e.email).filter((e): e is string => Boolean(e));
      if (emails.length > 0) {
        params.set('emails', emails.join(','));
      } else {
        params.delete('emails');
      }
    } else {
      params.delete('emails');
    }

    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const isFilterDisabled = disabled || (selectedGroups.length === 0 && selectedEmails.length === 0);

  return (
    <AutocompleteContainer>
      <AutocompleteWithLabel<DistributorGroup>
        options={groupsData ?? []}
        value={selectedGroups}
        inputValue={groupInputValue}
        loading={isGroupsLoading || isGroupsRefetching}
        onChange={handleGroupsChange}
        onInputChange={(_, input) => setGroupInputValue(input)}
        getOptionLabel={(option) => option.distributorName ?? ''}
        filterOptions={(x) => x}
        getOptionDisabled={(option) => selectedGroups.some((g) => g.distributorName === option.distributorName)}
        label='Filter by Distributors'
        disabled={disabled ?? false}
        testIdSuffix='distributors'
      />
      <AutocompleteWithLabel<UserAutocompleteResult>
        options={emailsData ?? []}
        value={selectedEmails}
        inputValue={emailInputValue}
        loading={isEmailsLoading || isEmailsRefetching}
        onChange={handleEmailsChange}
        onInputChange={(_, input) => setEmailInputValue(input)}
        getOptionLabel={(option) => option.email ?? ''}
        filterOptions={(x) => x}
        getOptionDisabled={(option) => selectedEmails.some((e) => e.email === option.email)}
        label='Filter by User Email'
        disabled={disabled ?? false}
        testIdSuffix='emails'
      />
      <AutocompleteFilterButton
        buttonLabel='Filter'
        buttonRef={filterButtonRef}
        onClick={handleFilterApply}
        isDisabled={isFilterDisabled}
      />
    </AutocompleteContainer>
  );
}

export default DistributorsFiltersAutocomplete;
